import { useCallback, useEffect, useMemo, useState } from "react";
import { Pager, fitArea, useFitPages } from "../ui/paging";
import {
  cheapestOf,
  type InventoryItem,
  type Supplier,
  type SupplierPrice,
} from "@dentacore/shared-types";

import {
  addSupplierPrice,
  createSupplier,
  listSuppliers,
  previewSupplierImport,
  removeSupplierPrice,
  runSupplierImport,
  updateSupplier,
  updateSupplierPrice,
} from "../api/suppliers";
import { formatMoney } from "../patients/money";
import { ImportDialog } from "./import-dialog";
import { ShelfList, type ShelfRow } from "./shelf-list";
import { PriceDialog, SupplierDialog } from "./supplier-dialogs";
import { atLocation } from "../api/client";
import { locate } from "../api/located";
import {
  Badges,
  CopySwitch,
  LocationField,
  across,
  groupByName,
  useShown,
} from "../shell/locations";
import type { Located } from "../shell/locations";
import { leadText, packUsesText, perUseText } from "./supplier-text";
import { useSeesPerUseCost } from "./use-sees-per-use-cost";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";

type Writer = (
  write: () => Promise<unknown>,
  reload: () => Promise<void>,
  done: string | ((result: unknown) => string),
) => Promise<boolean>;

/** Which prices are tied for cheapest for their item, across every supplier (SU-D1). */
export function cheapestIds(suppliers: readonly Supplier[]): {
  ids: Set<string>;
  tied: Set<string>;
} {
  const byItem = new Map<string, SupplierPrice[]>();
  for (const s of suppliers) {
    if (!s.isActive) continue;
    for (const p of s.prices) byItem.set(p.itemKey, [...(byItem.get(p.itemKey) ?? []), p]);
  }
  const ids = new Set<string>();
  const tied = new Set<string>();
  for (const prices of byItem.values()) {
    const best = cheapestOf(prices);
    for (const p of best) {
      ids.add(p.id);
      if (best.length > 1) tied.add(p.id);
    }
  }
  return { ids, tied };
}

/**
 * Inventory → *Suppliers* — STORY-171.
 *
 * The clinic's suppliers as a list, one opening beside it with its contact and
 * what it sells (the Inventory shape, IV5). **Add an item** sits beside
 * **Import supplier items from file** at the top of the panel, as Anthony asked
 * on the prototype. Changing anything is for whoever sets stock up (SU-D8);
 * everyone else reads.
 */
export function SuppliersTab({
  items,
  currency,
  canManage,
  busy,
  run,
  fail,
  onItemsChanged,
  resetOn,
  view = "people",
}: {
  /**
   * People → Suppliers (`people`) is the whole card: contact, edit, retire and
   * what they sell. Products → Supplier items (`items`) is what they sell and
   * nothing else — the same lines from the product side (MS-D8).
   */
  view?: "people" | "items";
  /** This location's items, for Add an item and the import's "same as". */
  items: readonly InventoryItem[];
  currency: string;
  canManage: boolean;
  busy: boolean;
  run: Writer;
  fail: (message: string) => void;
  /** A new item was created here, so the stock list must re-read. */
  onItemsChanged: () => Promise<void>;
  resetOn: unknown;
}): React.JSX.Element {
  const [suppliers, setSuppliers] = useState<readonly Supplier[] | null>(null);
  // Several locations shown: one row per supplier, matched by name.
  const places = useShown();
  const [addAt, setAddAt] = useState<string | null>(null);
  const read = useCallback(
    async (signal?: AbortSignal): Promise<readonly Supplier[]> => {
      if (!places.many) return listSuppliers(signal);
      const all = await across(places.ids, () => listSuppliers(signal));
      // A supplier's prices are changed at the supplier's own location.
      for (const supplier of all)
        for (const price of supplier.prices) locate(price.id, supplier.at);
      return all;
    },
    [places.key],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  // Price a use is a cost figure: only with Accounting (Emile, 2026-10-16).
  // Price per use needs Accounting and accounting.view (QF-D20).
  const hasAccounting = useSeesPerUseCost();

  const [dialog, setDialog] = useState<
    | { kind: "supplier"; supplier: Supplier | null }
    | { kind: "price"; price: SupplierPrice | null }
    | { kind: "import" }
    | null
  >(null);

  const reload = useCallback(async () => {
    setSuppliers(await read());
  }, [read]);

  useEffect(() => {
    const controller = new AbortController();
    read(controller.signal)
      .then(setSuppliers)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSuppliers([]);
        fail(cause instanceof Error ? cause.message : "Suppliers could not be loaded.");
      });
    return () => controller.abort();
  }, [fail, resetOn, read]);

  const best = useMemo(() => cheapestIds(suppliers ?? []), [suppliers]);
  const open = (suppliers ?? []).find((s) => s.id === openId) ?? null;
  // What they sell, searched by name (Emile, 2026-10-16) — cleared for each supplier.
  const [priceQuery, setPriceQuery] = useState("");
  useEffect(() => {
    setPriceQuery("");
  }, [openId]);
  const needle = priceQuery.trim().toLowerCase();
  const sold = (open?.prices ?? []).filter(
    (price) => needle === "" || price.itemName.toLowerCase().includes(needle),
  );
  // A supplier who sells forty things: whole rows, paged in the panel. Measured,
  // because a row grows a second line only when the supplier gave its details.
  const pricePages = useFitPages({
    keys: sold.map((price) => price.id),
    estimate: () => 46,
    gap: 0,
    resetOn: `${String(openId)}|${needle}`,
  });
  const [soldFrom, soldTo] = pricePages.range;

  /** The same supplier at each location shown, by the id of any copy. */
  const groups = useMemo(
    () => groupByName((suppliers ?? []) as Located<Supplier>[], (s) => s.name),
    [suppliers],
  );
  const copiesOf = new Map<string, Located<Supplier>[]>();
  for (const group of groups) for (const copy of group.copies) copiesOf.set(copy.id, group.copies);
  const listed = places.many ? groups.map((group) => group.first) : (suppliers ?? []);

  const rows: ShelfRow[] = listed.map((s) => {
    const copies = copiesOf.get(s.id) ?? [];
    const on = s.id === openId || copies.some((copy) => copy.id === openId);
    const wins = s.prices.filter((p) => best.ids.has(p.id)).length;
    return {
      key: s.id,
      node: (
        <button
          type="button"
          className={on ? `${inv.row} ${inv.rowOn}` : inv.row}
          aria-current={on ? "true" : undefined}
          onClick={() => setOpenId(on ? null : s.id)}
          data-card=""
          data-testid="supplier-row"
        >
          <span className={inv.rowName}>
            <span>
              {s.name}
              {!s.isActive && " (retired)"}
            </span>
            <Badges ats={copies.map((copy) => copy.at)} shown={places} />
          </span>
          <span className={inv.rowCount}>
            {places.many
              ? new Set(copies.flatMap((copy) => copy.prices.map((price) => price.id))).size
              : s.prices.length}
            {wins > 0 && <span className={styles.meta}>cheapest for {wins}</span>}
          </span>
        </button>
      ),
    };
  });

  return (
    <div className={inv.body}>
      <ShelfList
        testId="suppliers-list"
        head={
          <>
            <span className={inv.headLabel}>Suppliers</span>
            <span className={inv.headCount}>{listed.length}</span>
            {canManage && view === "people" && (
              <button
                type="button"
                className={`${inv.ghost} ${styles.pushRight}`}
                onClick={() => setDialog({ kind: "supplier", supplier: null })}
                data-testid="supplier-add"
              >
                Add a supplier
              </button>
            )}
          </>
        }
        columns={
          <div className={inv.columns} aria-hidden="true">
            <span>Supplier</span>
            <span className={inv.columnRight}>Items</span>
          </div>
        }
        rows={rows}
        empty={
          suppliers === null ? "Loading…" : "No suppliers yet. Add the first one to compare prices."
        }
        noun="suppliers"
        resetOn={resetOn}
      />

      {open !== null && (
        <aside
          className={`${inv.panel} ${styles.widePanel}`}
          aria-label={open.name}
          data-testid="supplier-panel"
        >
          <div className={inv.panelHead}>
            <h2 className={inv.panelTitle}>{open.name}</h2>
            <button
              type="button"
              className={inv.close}
              onClick={() => setOpenId(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          {canManage && (
            <div className={inv.actions}>
              <button
                type="button"
                className={inv.primary}
                onClick={() => setDialog({ kind: "price", price: null })}
                disabled={busy || !open.isActive}
                data-testid="supplier-add-item"
              >
                Add an item
              </button>
              <button
                type="button"
                className={inv.ghost}
                onClick={() => setDialog({ kind: "import" })}
                disabled={busy || !open.isActive}
                data-testid="supplier-import"
              >
                Import supplier items from file
              </button>
              {view === "people" && (
                <button
                  type="button"
                  className={inv.quiet}
                  onClick={() => setDialog({ kind: "supplier", supplier: open })}
                  disabled={busy}
                >
                  Edit
                </button>
              )}
              {view === "people" && (
                <button
                  type="button"
                  className={inv.quiet}
                  disabled={busy}
                  onClick={() => {
                    void run(
                      () => updateSupplier(open.id, { isActive: !open.isActive }),
                      reload,
                      open.isActive
                        ? `${open.name} retired. Their orders keep their history.`
                        : `${open.name} is back.`,
                    );
                  }}
                >
                  {open.isActive ? "Retire" : "Bring back"}
                </button>
              )}
            </div>
          )}

          {/* A supplier is the clinic's: the same record at every location needs no switch. */}
          {places.many &&
            new Set((copiesOf.get(open.id) ?? []).map((copy) => copy.id)).size > 1 && (
              <div className={styles.section}>
                <CopySwitch
                  copies={copiesOf.get(open.id) ?? []}
                  current={open.id}
                  shown={places}
                  onPick={setOpenId}
                />
              </div>
            )}
          {view === "people" && (
            <div className={styles.section}>
              <div className={styles.contact}>
                <span className={styles.contactKey}>Phone</span>
                <span>{open.phone ?? "—"}</span>
                <span className={styles.contactKey}>Email</span>
                <span>{open.email ?? "—"}</span>
                {open.note !== null && (
                  <>
                    <span className={styles.contactKey}>Note</span>
                    <span>{open.note}</span>
                  </>
                )}
              </div>
            </div>
          )}

          <div className={`${styles.section} ${styles.fill}`}>
            <div className={styles.sectionHead}>
              <h3 className={styles.sectionTitle}>What they sell</h3>
              {open.prices.length > 0 && (
                <input
                  type="search"
                  className={`${inv.search} ${styles.priceSearch}`}
                  value={priceQuery}
                  placeholder="Search their items…"
                  aria-label={`Search what ${open.name} sells`}
                  onChange={(event) => setPriceQuery(event.target.value)}
                  data-testid="supplier-prices-search"
                />
              )}
              <span className={inv.headCount}>
                {needle === ""
                  ? `${String(open.prices.length)} items`
                  : `${String(sold.length)} of ${String(open.prices.length)}`}
              </span>
            </div>
            {open.prices.length === 0 ? (
              <p className={styles.note}>Nothing listed yet.</p>
            ) : sold.length === 0 ? (
              <p className={styles.note} data-testid="supplier-prices-none">
                Nothing they sell matches that name.
              </p>
            ) : (
              <>
                {/*
                  **One calm line per item** (Emile, 2026-10-16: *"too crowded … I can't
                  guarantee that all suppliers will give me a detailed list"*). The name
                  and the price; a quiet second line only for what the supplier actually
                  gave — a pack of more than one, how long it takes — and never a filler.
                  The row itself opens Edit, as rows do elsewhere.
                */}
                <div className={fitArea} ref={pricePages.areaRef}>
                  <ul className={styles.sells} data-testid="supplier-prices">
                    {sold.slice(soldFrom, soldTo).map((price) => {
                      const details = [
                        price.usesPerPack > 1 ? packUsesText(price) : null,
                        hasAccounting && price.usesPerPack > 1
                          ? `${perUseText(price, currency)} a use`
                          : null,
                        leadText(price.leadDays),
                      ].filter((part): part is string => part !== null);
                      // The cheapest is the whole row in green and a word after the name
                      // (Emile, 2026-10-16: a word under the price went unnoticed).
                      const isBest = best.ids.has(price.id);
                      const rowClass = isBest
                        ? `${styles.sellsRow} ${styles.sellsRowBest}`
                        : styles.sellsRow;
                      const body = (
                        <>
                          <span className={styles.sellsName}>
                            <span className={styles.sellsItem}>
                              {price.itemName}
                              {isBest && (
                                <b className={styles.sellsBest}>
                                  {best.tied.has(price.id) ? " (same price)" : " (cheapest)"}
                                </b>
                              )}
                            </span>
                            {details.length > 0 && (
                              <span className={styles.sellsDetail}>{details.join(" · ")}</span>
                            )}
                          </span>
                          <span className={styles.sellsPrice}>
                            {formatMoney(price.priceCents, currency)}
                          </span>
                        </>
                      );
                      return (
                        <li key={price.id} ref={pricePages.measure(price.id)}>
                          {canManage ? (
                            <button
                              type="button"
                              className={rowClass}
                              onClick={() => setDialog({ kind: "price", price })}
                              aria-label={`Edit ${price.itemName}, ${formatMoney(price.priceCents, currency)}`}
                              data-testid={`price-edit-${price.id}`}
                            >
                              {body}
                            </button>
                          ) : (
                            <div className={rowClass}>{body}</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <Pager
                  compact
                  page={pricePages.page}
                  count={pricePages.pages.length}
                  onPage={pricePages.setPage}
                  from={soldFrom + 1}
                  to={soldTo}
                  total={sold.length}
                  noun="items"
                  testId="supplier-prices-pager"
                />
              </>
            )}
          </div>
        </aside>
      )}

      {dialog?.kind === "supplier" && (
        <SupplierDialog
          location={
            dialog.supplier === null ? (
              <LocationField shown={places} value={addAt} onChange={setAddAt} />
            ) : undefined
          }
          supplier={dialog.supplier}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(body) => {
            const editing = dialog.supplier;
            const made: { id: string | null } = { id: null };
            return run(
              async () => {
                made.id = (
                  editing === null
                    ? await atLocation(places.many ? (addAt ?? places.active) : null, () =>
                        createSupplier(body),
                      )
                    : await updateSupplier(editing.id, body)
                ).id;
              },
              reload,
              editing === null ? `${body.name} added.` : "Saved.",
            ).then((landed) => {
              if (landed && made.id !== null) setOpenId(made.id);
              return landed;
            });
          }}
        />
      )}

      {dialog?.kind === "price" && open !== null && (
        <PriceDialog
          supplier={open}
          price={dialog.price}
          items={items}
          currency={currency}
          busy={busy}
          onClose={() => setDialog(null)}
          onRemove={() => {
            const price = dialog.price;
            if (price === null) return;
            void run(
              () => removeSupplierPrice(price.id),
              reload,
              `${price.itemName} taken off ${open.name}'s list.`,
            ).then((landed) => {
              if (landed) setDialog(null);
            });
          }}
          onSave={(body) => {
            const price = dialog.price;
            return run(
              () =>
                price === null
                  ? addSupplierPrice(open.id, body)
                  : updateSupplierPrice(price.id, {
                      pack: body.pack,
                      usesPerPack: body.usesPerPack,
                      priceCents: body.priceCents,
                      leadDays: body.leadDays ?? null,
                    }),
              async () => {
                await reload();
                if (body.createItem !== undefined) await onItemsChanged();
              },
              body.createItem !== undefined
                ? `${body.itemName} added to Inventory and to ${open.name}'s list.`
                : "Saved. Who is cheapest is worked out again from the new price.",
            );
          }}
        />
      )}

      {dialog?.kind === "import" && open !== null && (
        <ImportDialog
          supplier={open}
          items={items}
          currency={currency}
          onClose={() => setDialog(null)}
          onPreview={(body) => previewSupplierImport(open.id, body)}
          onImport={(body) =>
            run(
              () => runSupplierImport(open.id, body),
              async () => {
                await reload();
                await onItemsChanged();
              },
              (result) => {
                const r = result as { added: number; changed: number; created: number };
                return `Imported: ${String(r.added)} new prices, ${String(r.changed)} price changes, ${String(r.created)} new items.`;
              },
            )
          }
        />
      )}
    </div>
  );
}
